import { gql } from '@apollo/client/core';
import { computed, reactive, toRefs } from 'vue';
import { executeQuery } from '@/components/commons/apolloUtils';
import UserService from '@/services/UserService';
import useMutation from '@/compositions/useMutation';
import useNotFound from '@/compositions/useNotFound';
import Tracking from '@/components/commons/Tracking';
import LocalStorage, { LocalStorageKeys } from '@/components/commons/LocalStorage';

const URL_DISCOUNT_QUERY = gql`
  query URL_DISCOUNT($discountName: String!) {
    getUrlDiscount(discountName: $discountName) {
      title
      htmlContent
      active
      isActive
      featureImage
      discountCampaignType
      discountEligibility
      loggedInMessage
    }
  }
`;

const { mutate: saveCustomerDiscountRequest } = useMutation(gql`
  mutation SAVE_CUSTOMER_DISCOUNT($discountName: String!) {
    saveCustomerDiscount(discountName: $discountName) {
      success
    }
  }
`);

const state = reactive({
  discountCode: computed(() => LocalStorage.get(LocalStorageKeys.DISCOUNT_CODE)),
  discount: null,
});

export default function useDiscount() {
  const { showNotFoundPage } = useNotFound();

  async function processUrlDiscount({ discountName, campaignType }) {
    const urlDiscount = await fetchUrlDiscount(discountName, campaignType);

    if (urlDiscount) {
      updateDiscount(urlDiscount);
      await saveCustomerDiscount();
    } else {
      updateDiscount();
      showNotFoundPage({reason: `Invalid url discount ${discountName}`});
    }
  }

  async function fetchUrlDiscount(discountName, campaignType) {
    if (!discountName) {
      return;
    }

    const { getUrlDiscount } = await executeQuery(URL_DISCOUNT_QUERY, { discountName });
    if (getUrlDiscount?.active && campaignType === getUrlDiscount.discountCampaignType) {
      return {
        code: discountName,
        name: getUrlDiscount.title,
        message: getUrlDiscount.htmlContent,
        imageUrl: getUrlDiscount.featureImage,
        isActive: getUrlDiscount.isActive,
        discountCampaignType: getUrlDiscount.discountCampaignType,
        discountEligibility: getUrlDiscount.discountEligibility,
        loggedInMessage: getUrlDiscount.loggedInMessage,
      };
    }
  }

  async function saveCustomerDiscount() {
    if (state.discount && UserService.isVerified() && UserService.getCustomerToken()) {
      try {
        await saveCustomerDiscountRequest({ discountName: state.discount.code });
      } catch (error) {
        await Tracking.sentryCaptureError(error);
      }
    }
  }

  function updateDiscount(payload) {
    state.discount = payload;
    if (payload?.code) {
      LocalStorage.set(LocalStorageKeys.DISCOUNT_CODE, payload?.code);
    } else {
      LocalStorage.remove(LocalStorageKeys.DISCOUNT_CODE);
    }
  }

  return {
    ...toRefs(state),
    processUrlDiscount,
    updateDiscount,
    saveCustomerDiscount,
  };
}
